/**
 * @fileoverview Move Limit Driver - 手数制限ドライバ（Classic モード）
 * RAF は使用せず、presentation層から update(nowMs) が呼ばれる
 *
 * ## 状態管理
 * 単一フェーズフィールドで状態を表現:
 * - LIMIT_RUNTIME_PHASE: 'idle' | 'intro' | 'running' | 'consumed'
 *
 * ## 状態遷移
 * idle (Title画面)
 *   ↓ start()
 * intro (イントロアニメーション中)
 *   ↓ update() でイントロ完了
 * running (手数消費可能、イベント駆動)
 *   ↓ decrementMove() でゼロ到達
 * consumed (制限到達、GameOver待ち)
 *   ↓ stop()
 * idle (Title画面に戻る)
 */

import { DEFAULT_LIMIT_VALUES, INTRO_ANIMATION } from '../../shared/constants/limit-defaults.js';
import { STATE_PATHS } from '../../shared/constants/state-paths.js';

import { createInterpolator, easings } from './interpolator.js';

let introInterpolator = null;
let decrementInterpolator = null;
let introCompleteCallback = null;

export function configure(gameState, options) {
  const moves = Math.max(1, Number(options.maxMoves) || DEFAULT_LIMIT_VALUES.MAX_MOVES);
  gameState.setState(STATE_PATHS.LIMIT_CONFIG_MAX_MOVES, moves);
}

export function start(gameState, onIntroComplete = null) {
  // 冪等性保証: 既存状態をクリア（reset統合）
  if (introInterpolator) {
    introInterpolator.stop();
    introInterpolator = null;
  }
  if (decrementInterpolator) {
    decrementInterpolator.stop();
    decrementInterpolator = null;
  }
  introCompleteCallback = null;

  // 初期値設定（最大値を残り値にセット）
  const maxMoves =
    gameState.getState(STATE_PATHS.LIMIT_CONFIG_MAX_MOVES) || DEFAULT_LIMIT_VALUES.MAX_MOVES;
  gameState.setState(STATE_PATHS.LIMIT_RUNTIME_REMAINING_VALUE, maxMoves);

  // イントロフェーズへ遷移
  gameState.setState(STATE_PATHS.LIMIT_RUNTIME_PHASE, 'intro');
  introCompleteCallback = onIntroComplete;

  introInterpolator = createInterpolator({
    from: 0,
    to: 1,
    durationMs: INTRO_ANIMATION.DURATION_MS,
    easingFn: easings.cubicOut,
  });

  introInterpolator.start(performance.now());
}

export function stop(gameState) {
  // 1. 最終集計（move-limitではno-op）

  // 2. 停止処理
  if (introInterpolator) {
    introInterpolator.stop();
  }
  if (decrementInterpolator) {
    decrementInterpolator.stop();
  }

  // 3. idle状態へ遷移
  gameState.setState(STATE_PATHS.LIMIT_RUNTIME_PHASE, 'idle');
  gameState.setState(STATE_PATHS.LIMIT_RUNTIME_REMAINING_VALUE, 0);
}

export function update(gameState, nowMs) {
  // イントロ補間更新
  if (introInterpolator && introInterpolator.isRunning()) {
    const continuing = introInterpolator.update(nowMs);
    if (!continuing) {
      // イントロ完了 → runningフェーズへ遷移
      gameState.setState(STATE_PATHS.LIMIT_RUNTIME_PHASE, 'running');

      if (typeof introCompleteCallback === 'function') {
        introCompleteCallback();
        introCompleteCallback = null;
      }
    }
  }

  // 減少補間更新
  if (decrementInterpolator && decrementInterpolator.isRunning()) {
    decrementInterpolator.update(nowMs);
    // NOTE: 最終値は decrementMove() 内で LIMIT_RUNTIME_REMAINING_VALUE に既に書き込み済み
  }
}

/**
 * 残り手数を取得（内部ヘルパー、小数値）
 * アニメーション中は補間値をそのまま返す（整数化なし）
 */
function getRemainingValueRaw(gameState) {
  if (decrementInterpolator && decrementInterpolator.isRunning()) {
    return decrementInterpolator.getValue();
  }
  return gameState.getState(STATE_PATHS.LIMIT_RUNTIME_REMAINING_VALUE) || 0;
}

/**
 * 残り手数を取得（外部API、整数値）
 * 数値表示用に整数化
 */
export function getRemainingValue(gameState) {
  return Math.floor(getRemainingValueRaw(gameState));
}

/**
 * プログレスバー用の進捗率を取得（0.0〜1.0）
 * アニメーション中は小数値の補間値を使用（スムーズな減少）
 */
export function getProgressRatio(gameState) {
  const max =
    gameState.getState(STATE_PATHS.LIMIT_CONFIG_MAX_MOVES) || DEFAULT_LIMIT_VALUES.MAX_MOVES;
  if (max <= 0) return 0;

  const remaining = getRemainingValueRaw(gameState); // 小数値を直接使用
  const clampedRemaining = Math.max(0, Math.min(max, remaining));
  return clampedRemaining / max;
}

export function getType() {
  return 'move';
}

export function getDisplayLabel() {
  return 'moves';
}

export function getIntroProgress() {
  return introInterpolator ? introInterpolator.getProgress() : 0;
}

export function isIntroAnimating(gameState) {
  return gameState.getState(STATE_PATHS.LIMIT_RUNTIME_PHASE) === 'intro';
}

export function decrementMove(gameState) {
  const remaining = gameState.getState(STATE_PATHS.LIMIT_RUNTIME_REMAINING_VALUE) || 0;
  if (remaining <= 0) return;

  const newRemaining = remaining - 1;

  // GameState更新（即座に反映）
  gameState.setState(STATE_PATHS.LIMIT_RUNTIME_REMAINING_VALUE, newRemaining);

  // 多重アニメーション対応：進行中なら現在の補間値から再開
  const fromNow =
    decrementInterpolator && decrementInterpolator.isRunning()
      ? decrementInterpolator.getValue()
      : remaining;

  decrementInterpolator = createInterpolator({
    from: fromNow,
    to: newRemaining,
    durationMs: 400,
    easingFn: easings.quadOut,
  });

  decrementInterpolator.start(performance.now());

  // ゼロになったらconsumedフェーズへ遷移（コールバックはReady復帰時に発火）
  if (newRemaining <= 0) {
    const phase = gameState.getState(STATE_PATHS.LIMIT_RUNTIME_PHASE);
    if (phase !== 'consumed') {
      gameState.setState(STATE_PATHS.LIMIT_RUNTIME_PHASE, 'consumed');
      // NOTE: callback() は呼ばない
      // GameOver発火は piece-interaction.js の finally 節で Ready 状態になった時に行う
    }
  }
}

export function initializeVisibilityHandling() {
  // Move版では不要（イベント駆動のため）
}
